import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Reward = () => {
  const navigate = useNavigate();
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showGameModal, setShowGameModal] = useState(false);
  const [games, setGames] = useState({});
  const [selectedGame, setSelectedGame] = useState('');
  const [selectedGameKey, setSelectedGameKey] = useState('');
  const [loadingGames, setLoadingGames] = useState(false);
  const [claimSuccessMessage, setClaimSuccessMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const pageSize = 10;

  // Check for token and redirect if not present
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
    }
  }, [navigate]);

  // Fetch user profile
  const fetchUserProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    setProfileLoading(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow"
      };

      const response = await fetch("https://devapi.quickcare.ng/api/v1/user/profile", requestOptions);

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate('/');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setUserProfile(result.data);
      } else {
        console.error('Failed to fetch profile:', result.message);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setProfileLoading(false);
    }
  };

  // Fetch games
  const fetchGames = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    setLoadingGames(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow"
      };

      const response = await fetch("https://devapi.quickcare.ng/api/v1/games/keys", requestOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setGames(result.data);
        console.log('Games fetched:', result.data);
      } else {
        console.error('Failed to fetch games:', result.message);
      }
    } catch (err) {
      console.error('Error fetching games:', err);
    } finally {
      setLoadingGames(false);
    }
  };

  // Fetch rewards
  const fetchRewards = async (page) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow"
      };

      const response = await fetch(
        `https://devapi.quickcare.ng/api/v1/rewards?page=${page}&page_size=${pageSize}`,
        requestOptions
      );

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate('/');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setRewards(result.data.data);
        setCurrentPage(result.data.page);
        setHasNextPage(result.data.next_page);
      } else {
        setError(result.message || 'Failed to fetch rewards');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchUserProfile();
    fetchRewards(currentPage);
  }, [currentPage]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleRewardClick = (reward) => {
    setSelectedReward(reward);
  };

  const closeModal = () => {
    setSelectedReward(null);
    setClaimingId(null);
  };

  const handleClaimReward = async () => {
    if (!selectedReward) return;
    
    // Log the reward ID
    console.log('Reward ID:', selectedReward.id);
    
    // Fetch games and show modal
    await fetchGames();
    setShowGameModal(true);
  };

  const handleGameSelect = (gameKey, gameName) => {
    setSelectedGame(gameName);
    setSelectedGameKey(gameKey);
    console.log('Selected game key:', gameKey);
    console.log('Selected game name:', gameName);
  };

  const handleConfirmClaim = async () => {
    if (!selectedGameKey) {
      alert('Please select a game to claim your reward');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    setClaimingId(selectedReward.id);

    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const raw = JSON.stringify({
        "id": selectedReward.id,
        "game_key": selectedGameKey
      });

      console.log('Claim request payload:', raw);

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
      };

      const response = await fetch("https://devapi.quickcare.ng/api/v1/rewards/claim", requestOptions);
      const result = await response.json();

      console.log('Claim response:', result);

      if (response.ok && result.success) {
        // Show success message
        setClaimSuccessMessage(result.message || `Successfully claimed ${selectedReward.amount} ${selectedReward.type} for ${selectedGame}!`);
        setShowSuccessModal(true);
        
        // Refresh rewards list
        await fetchRewards(currentPage);
        
        // Close modals
        setShowGameModal(false);
        closeModal();
        setSelectedGame('');
        setSelectedGameKey('');
      } else if (response.status === 401) {
        localStorage.removeItem('token');
        navigate('/');
      } else {
        // Show error message from server
        alert(result.message || 'Failed to claim reward. Please try again.');
      }
    } catch (err) {
      console.error('Claim API error:', err);
      alert('An error occurred while claiming the reward. Please try again.');
    } finally {
      setClaimingId(null);
    }
  };

  const closeGameModal = () => {
    setShowGameModal(false);
    setSelectedGame('');
    setSelectedGameKey('');
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setClaimSuccessMessage('');
  };

  const handleCopyReferralCode = () => {
    if (userProfile?.referral_code) {
      navigator.clipboard.writeText(userProfile.referral_code);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'referral':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  if ((loading && rewards.length === 0) || profileLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#069125]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 instrument-sans-medium">
      {/* Referral Banner */}
      {userProfile && (
       <div className='w-full flex flex-col gap-2 lg:w-1/2 mb-8'>
            <p className='font-medium text-[#828282] '>
                Your invite Code
            </p>

            <div className='w-full flex gap-2 border-2 border-[#DEDEDE] items-center justify-between  py-1 px-2 rounded-lg'>
                <p className='instrument-sans-semibold tracking-wider'>
                    {userProfile.referral_code}
                </p>
                <button
                    onClick={handleCopyReferralCode}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Copy code"
                  >
                    {copySuccess ? (
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <button className='min-w-[125px] min-h-[44px] flex items-center justify-center rounded bg-[#069125] text-white'>
                        Copy link
                      </button>
                    )}
                  </button>
            </div>
       </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl instrument-sans-bold  text-[#069125]">
          My Rewards
        </h1>
        <p className="text-gray-500 mt-2">View and claim your rewards from referrals and activities</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => fetchRewards(currentPage)}
            className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
          >
            Try Again →
          </button>
        </div>
      )}

      {/* Rewards Grid */}
      {!loading && rewards.length === 0 && !error ? (
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-12 text-center">
          <div className="w-24 h-24 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No rewards yet</h3>
          <p className="text-gray-500">Invite friends using your referral code to earn rewards!</p>
          {userProfile?.referral_code && (
            <button
              onClick={() => setShowReferralModal(true)}
              className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Share Referral Code
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rewards.map((reward) => (
            <div
              key={reward.id}
              onClick={() => handleRewardClick(reward)}
              className="group bg-white rounded shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer transform hover:-translate-y-1"
            >
              <div className="relative p-6">
                {/* Status Badge */}
                <div className="absolute top-4 right-4">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    reward.status === 'claimed' 
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {reward.status === 'claimed' ? 'Claimed' : 'Unclaimed'}
                  </span>
                </div>

                {/* Reward Icon */}
                <div className="w-16 h-16 rounded-full bg-[#069125]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  {getSourceIcon(reward.source)}
                </div>

                {/* Amount */}
                <div className="mb-3">
                  <span className="text-3xl font-bold text-gray-800">{reward.amount}</span>
                  <span className="text-gray-500 ml-1 capitalize">{reward.type.replace('_', ' ')}</span>
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm mb-2 line-clamp-2">{reward.description}</p>

                {/* Source & Date */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-medium text-[#069125] capitalize bg-[#069125]/10 px-2 py-1 rounded-full">
                      {reward.source}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>{formatDate(reward.created_at).split(',')[0]}</span>
                  </div>
                </div>

                {/* Claim Button Overlay */}
                {reward.status === 'unclaimed' && (
                  <div className="mt-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRewardClick(reward);
                      }}
                      className="w-full py-2 bg-[#069125] text-white rounded font-medium text-sm hover:opacity-90 transition-opacity min-h-[44px]"
                    >
                      View & Claim
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {rewards.length > 0 && (
        <div className="flex justify-center items-center gap-4 mt-10">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className={`flex items-center gap-2 px-5 py-2.5 rounded font-medium transition-all duration-200 ${
              currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-200 text-gray-700 hover:border-purple-300 hover:text-purple-600 shadow-sm'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          <span className="px-4 py-2  text-[#069125] text-center lg:text-base rounded font-medium">
            Page {currentPage}
          </span>
          <button
            onClick={handleNextPage}
            disabled={!hasNextPage}
            className={`flex items-center gap-2 px-5 py-2.5 rounded font-medium transition-all duration-200 ${
              !hasNextPage
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-200 text-gray-700 hover:border-purple-300 hover:text-purple-600 shadow-sm'
            }`}
          >
            Next
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Reward Detail Modal */}
      {selectedReward && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl transform transition-all" onClick={(e) => e.stopPropagation()}>
            <div className="relative p-6">
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="text-center mb-6">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#069125]/10 to-pink-100 flex items-center justify-center mb-4">
                  {getSourceIcon(selectedReward.source)}
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Reward Details</h2>
                <div className="mt-2">
                  <span className="text-4xl font-bold text-[#069125]">{selectedReward.amount}</span>
                  <span className="text-gray-500 ml-1 capitalize">{selectedReward.type.replace('_', ' ')}</span>
                </div>
              </div>

              <div className="space-y-3 border-t border-gray-100 pt-4">
                <div className="flex justify-between py-2">
                  <span className="text-gray-500">Description</span>
                  <span className="text-gray-800 font-medium">{selectedReward.description}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-500">Source</span>
                  <span className="capitalize bg-[#069125]/10 text-[#069125] px-3 py-1 rounded-full text-sm">{selectedReward.source}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-500">Game</span>
                  <span className="capitalize text-gray-800">{selectedReward.game}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-500">Status</span>
                  <span className={`capitalize px-3 py-1 rounded-full text-sm ${
                    selectedReward.status === 'claimed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {selectedReward.status}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-500">Date Received</span>
                  <span className="text-gray-800">{formatDate(selectedReward.created_at)}</span>
                </div>
              </div>

              <div className="mt-6">
                {selectedReward.status === 'unclaimed' ? (
                  <button
                    onClick={handleClaimReward}
                    disabled={claimingId === selectedReward.id}
                    className="w-full py-3 bg-[#069125] text-white rounded font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {claimingId === selectedReward.id ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Claiming...
                      </div>
                    ) : (
                      'Claim Reward'
                    )}
                  </button>
                ) : (
                  <button
                    onClick={closeModal}
                    className="w-full py-3 bg-gray-100 text-gray-600 rounded font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Game Selection Modal */}
      {showGameModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeGameModal}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl transform transition-all" onClick={(e) => e.stopPropagation()}>
            <div className="relative p-6">
              <button
                onClick={closeGameModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="text-center mb-6">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#069125]/10 flex items-center justify-center mb-4">
                  <svg className="w-10 h-10 text-[#069125]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Select Game</h2>
                <p className="text-gray-500 mt-2">Choose which game to attach this reward to</p>
                {selectedReward && (
                  <p className="text-sm text-gray-600 mt-2">
                    Reward: <span className="font-semibold text-[#069125]">{selectedReward.amount} {selectedReward.type.replace(/_/g, ' ')}</span>
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Game
                  </label>
                  {loadingGames ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#069125]"></div>
                    </div>
                  ) : (
                    <select
                      value={selectedGameKey}
                      onChange={(e) => {
                        const gameKey = e.target.value;
                        const gameName = games[gameKey];
                        handleGameSelect(gameKey, gameName);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#069125] focus:border-transparent"
                    >
                      <option value="">Select a game...</option>
                      {Object.entries(games).map(([key, name]) => (
                        <option key={key} value={key}>
                          {name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {selectedGame && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      You selected: <span className="font-semibold">{selectedGame}</span>
                    </p>
                    
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={closeGameModal}
                  className="flex-1 py-3 bg-gray-100 text-gray-600 rounded font-semibold hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmClaim}
                  disabled={!selectedGameKey || claimingId === selectedReward?.id}
                  className="flex-1 py-3 bg-[#069125] text-white rounded font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {claimingId === selectedReward?.id ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Claiming...
                    </div>
                  ) : (
                    'Claim Reward'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeSuccessModal}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl transform transition-all" onClick={(e) => e.stopPropagation()}>
            <div className="relative p-6">
              <button
                onClick={closeSuccessModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Success!</h2>
                <p className="text-gray-600 mb-6">{claimSuccessMessage}</p>
                <button
                  onClick={closeSuccessModal}
                  className="w-full py-3 bg-[#069125] text-white rounded font-semibold hover:opacity-90 transition-opacity"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reward;